import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Pricing() {
  const { t } = useI18n();
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/credit-packs").then(({ data }) => setPacks(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const choose = async (code) => {
    setBusy(code);
    try { await api.post(`/billing/change-plan/${code}`); await refreshUser(); toast.success(t("common.success")); }
    catch (e) { toast.error(t("common.error")); } finally { setBusy(""); }
  };
  const buy = async (code) => {
    setBusy(code);
    try { const { data } = await api.post(`/billing/buy-credits/${code}`); await refreshUser(); toast.success(`+${data.added} ${t("common.credits")}`); }
    catch (e) { toast.error(t("common.error")); } finally { setBusy(""); }
  };

  const current = user?.plan?.code;

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader title={t("pricing.title")} subtitle={t("pricing.subtitle")} />
      <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-sm px-4 py-2.5 mb-6" data-testid="mock-payment-note">
        {t("pricing.mockNote")}
      </div>
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {plans.map((p, i) => (
          <div key={p.code} className={`rounded-2xl border p-6 bg-card flex flex-col ${current === p.code ? "ring-2 ring-primary" : ""}`} data-testid={`pricing-plan-${p.code}`}>
            <h3 className="font-display text-lg font-semibold">{p.name}</h3>
            <div className="mt-2"><span className="font-display text-2xl font-bold">{p.price_monthly}</span><span className="text-muted-foreground text-sm"> {p.currency}{t("pricing.month")}</span></div>
            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-primary" />{p.monthly_credits} {t("pricing.perMonth")}</p>
            <ul className="mt-4 space-y-1.5 text-sm flex-1">
              {Object.entries(p.features || {}).filter(([, v]) => v === true).map(([k]) => (
                <li key={k} className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{k.replace(/_/g, " ")}</li>
              ))}
            </ul>
            <Button
              className="mt-5 rounded-full w-full"
              variant={current === p.code ? "outline" : "default"}
              disabled={current === p.code || p.price_monthly > 0 || busy === p.code}
              onClick={() => choose(p.code)}
              data-testid={`choose-plan-${p.code}`}
            >
              {current === p.code
                ? t("pricing.currentPlan")
                : p.price_monthly > 0
                  ? t("pricing.comingSoon")
                  : t("pricing.choose")}
            </Button>
          </div>
        ))}
      </div>

      <h2 className="font-display text-xl font-semibold mt-12 mb-4">{t("pricing.creditsPacks")}</h2>
      <div className="grid gap-4 sm:grid-cols-3 max-w-2xl">
        {packs.map((pk) => (
          <div key={pk.code} className="rounded-2xl border bg-card p-5 text-center" data-testid={`credit-pack-${pk.code}`}>
            <div className="font-display text-2xl font-bold text-primary">{pk.credits}</div>
            <div className="text-xs text-muted-foreground">{t("common.credits")}</div>
            <div className="mt-2 font-medium">{pk.price} {pk.currency}</div>
            <Button
              className="mt-3 rounded-full w-full"
              variant="outline"
              disabled
              data-testid={`buy-pack-${pk.code}`}
            >
              {t("pricing.comingSoon")}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
